/**
 * Shared field validators. Deliberately mirrors the rules in the Infinity
 * Touch app (`InfinityTouchApp/src/core/validation.ts`) so the app and the
 * customer panel accept exactly the same values.
 */

/**
 * Engraving lines are physically engraved on the product, so the limit is set
 * by what fits — 12 characters per line. Enforced in both ordering paths (the
 * customer panel and the funeral home's print-order flow) and again on the API.
 */
export const ENGRAVING_MAX_LEN = 12;

/** US ZIP — exactly 5 digits. ZIP+4 is not accepted. */
export const US_ZIP_REGEX = /^\d{5}$/;

export const isValidUsZip = (value) => US_ZIP_REGEX.test(String(value ?? '').trim());

/**
 * US phone — tolerates the usual formatting (spaces, dashes, parens, dots)
 * and validates on the digit count, which must be exactly 10. Note this is a
 * digit count, not a string length: "(555) 12" is 8 characters but only 5
 * digits, and must not pass.
 */
export const isValidUsPhone = (value) =>
  String(value ?? '').replace(/\D/g, '').length === 10;

export const PHONE_ERROR = 'Enter a 10-digit phone number';
export const ZIP_ERROR = 'Enter a 5-digit zip code';

/**
 * Email — same rule as the app (`InfinityTouchApp/src/core/validation.ts`):
 * something, an @, something, a dot, something, and no whitespace anywhere.
 * Deliberately loose. A stricter pattern rejects addresses that are perfectly
 * deliverable, and the only authority on whether an address exists is sending
 * to it; this is here to catch a typo, not to prove reachability.
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (value) => EMAIL_REGEX.test(String(value ?? '').trim());

export const EMAIL_ERROR = 'Enter a valid email address';

/**
 * As-you-type companions to the validators above.
 *
 * The predicates only run on submit, which meant a user could type "abcde" into
 * a ZIP box and only learn it was wrong after pressing Save. These strip
 * everything that is not a digit and cut the result to length, so the field
 * cannot hold an invalid value in the first place — the same feel as the
 * engraving box, which is capped at ENGRAVING_MAX_LEN as you type.
 *
 * Note `maxLength` on the input alone is not enough: it limits how many
 * characters fit but happily accepts letters.
 */
export const PHONE_MAX_DIGITS = 10;
export const ZIP_MAX_DIGITS = 5;

export const digitsOnly = (value, max) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return max ? digits.slice(0, max) : digits;
};

/**
 * As-you-type companion to `isValidPostalCode`. The 5-digit rule is US-only, so
 * this has to branch on country: applying it everywhere would make a Canadian
 * "A1A 1A1" literally impossible to type.
 */
export const CA_POSTAL_MAX_LEN = 7; // "A1A 1A1"

export const sanitizePostalCode = (value, country = 'US') => {
  if (String(country ?? 'US').toUpperCase() === 'US') return digitsOnly(value, ZIP_MAX_DIGITS);
  return String(value ?? '').replace(/[^A-Za-z0-9 ]/g, '').toUpperCase().slice(0, CA_POSTAL_MAX_LEN);
};

export const postalCodeMaxLen = (country = 'US') =>
  (String(country ?? 'US').toUpperCase() === 'US' ? ZIP_MAX_DIGITS : CA_POSTAL_MAX_LEN);

/**
 * Postal code check for an address. The 5-digit rule is US-only — Canada is
 * also selectable at checkout and uses "A1A 1A1", so a non-US address is only
 * required to be non-empty, as before.
 */
export const isValidPostalCode = (value, country = 'US') => {
  const trimmed = String(value ?? '').trim();
  if (String(country ?? 'US').toUpperCase() === 'US') return isValidUsZip(trimmed);
  return trimmed.length > 0;
};
